import { cn } from "../../../utils/cn.js";
import { Button } from "../Button/Button.js";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover.js";
import { Calendar } from "../Calendar/Calendar.js";
import { TextInput } from "../TextInput/TextInput.js";
import { CalendarBlankIcon } from "@phosphor-icons/react";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { addDays, differenceInCalendarDays, endOfDay, format, isAfter, isValid, min, parse, startOfDay, subMonths } from "date-fns";
//#region src/components/DateRangePicker/DateRangePicker.tsx
const DateRangePickerContext = React.createContext(null);
function useDateRangePicker() {
	const context = React.useContext(DateRangePickerContext);
	if (!context) throw new Error("useDateRangePicker must be used within a DateRangePicker");
	return context;
}
function clampRangeToMax(from, to, maxInclusiveEnd) {
	const t = min([to, endOfDay(maxInclusiveEnd)]);
	return {
		from: min([from, t]),
		to: t
	};
}
function applyTimeToDate(base, timeHHmm) {
	const [h, m] = timeHHmm.split(":").map(Number);
	if (Number.isNaN(h) || Number.isNaN(m)) return base;
	const next = new Date(base);
	next.setHours(h, m, 0, 0);
	return next;
}
function tryParseDateString(raw, dateDisplayFormat, reference) {
	const trimmed = raw.trim();
	if (!trimmed) return void 0;
	const parsed = parse(trimmed, dateDisplayFormat, reference);
	return isValid(parsed) ? parsed : void 0;
}
function DateRangePicker({ align = "end", children, maxDate, onValueChange, triggerDateFormat = "MMM d, yyyy", value }) {
	const [open, setOpenState] = React.useState(false);
	const [calendarMountKey, setCalendarMountKey] = React.useState(0);
	const [draft, setDraft] = React.useState({
		from: value.from,
		to: value.to
	});
	const setOpen = React.useCallback((next) => {
		setOpenState(next);
		if (next) {
			setDraft({
				from: value.from,
				to: value.to
			});
			setCalendarMountKey((k) => k + 1);
		}
	}, [value.from, value.to]);
	const commitRange = React.useCallback((from, to) => {
		const clamped = maxDate ? clampRangeToMax(from, to, maxDate) : {
			from,
			to
		};
		onValueChange(clamped.from, clamped.to);
	}, [maxDate, onValueChange]);
	const commit = React.useCallback((range) => {
		const from = range?.from ?? draft.from;
		if (!from) return;
		commitRange(from, range?.to ?? draft.to ?? from);
		setOpenState(false);
	}, [
		commitRange,
		draft.from,
		draft.to
	]);
	const cancel = React.useCallback(() => setOpenState(false), []);
	const clear = React.useCallback(() => setDraft({}), []);
	const stepBySpan = React.useCallback((direction) => {
		const span = differenceInCalendarDays(value.to, value.from) + 1;
		commitRange(addDays(value.from, span * direction), addDays(value.to, span * direction));
	}, [
		commitRange,
		value.from,
		value.to
	]);
	const label = React.useMemo(() => {
		const a = format(value.from, triggerDateFormat);
		if (value.from.getTime() === value.to.getTime()) return a;
		return `${a} – ${format(value.to, triggerDateFormat)}`;
	}, [
		triggerDateFormat,
		value.from,
		value.to
	]);
	const context = React.useMemo(() => ({
		align,
		calendarMountKey,
		cancel,
		clear,
		commit,
		draft,
		label,
		maxDate,
		open,
		setDraft,
		setOpen,
		stepBySpan
	}), [
		align,
		calendarMountKey,
		cancel,
		clear,
		commit,
		draft,
		label,
		maxDate,
		open,
		setOpen,
		stepBySpan
	]);
	return /* @__PURE__ */ jsx(DateRangePickerContext.Provider, {
		value: context,
		children: /* @__PURE__ */ jsx(Popover, {
			open,
			onOpenChange: setOpen,
			children
		})
	});
}
function DateRangePickerTrigger({ render }) {
	const { label, open } = useDateRangePicker();
	const defaultTrigger = /* @__PURE__ */ jsxs(Button, {
		type: "button",
		variant: "secondary",
		size: "small",
		children: [/* @__PURE__ */ jsx(CalendarBlankIcon, {
			className: "size-[14px]",
			weight: "regular"
		}), label]
	});
	return /* @__PURE__ */ jsx(PopoverTrigger, { render: render?.({
		label,
		open
	}) ?? defaultTrigger });
}
function DateRangePickerContent({ children, className, ...props }) {
	const { align } = useDateRangePicker();
	return /* @__PURE__ */ jsx(PopoverContent, {
		align,
		className: cn("max-h-[min(680px,calc(100vh-3rem))] w-[min(calc(100vw-2rem),720px)] overflow-hidden", className),
		side: "bottom",
		sideOffset: 6,
		...props,
		children
	});
}
function DateRangePickerPresets({ onSelect = "commit", presets }) {
	const { commit, setDraft } = useDateRangePicker();
	const handlePreset = (preset) => {
		const range = preset.getRange();
		if (onSelect === "commit") commit(range);
		else setDraft(range);
	};
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": "Quick date ranges",
		className: "flex w-40 shrink-0 flex-col gap-0 overflow-y-auto border-r border-r-(length:--border-width-default) border-border-primary bg-elevation-surface-overlay py-md px-xs",
		children: presets.map((p) => /* @__PURE__ */ jsx("button", {
			className: cn("flex min-h-[36px] w-full cursor-pointer items-center rounded-xs", "bg-elevation-surface-overlay px-xs py-xxs text-left", "text-body-small leading-body-small font-heading-medium text-text-primary", "transition-colors hover:bg-background-neutral-secondary-hover"),
			type: "button",
			onClick: () => handlePreset(p),
			children: p.label
		}, p.label))
	});
}
function DateRangePickerFields({ dateDisplayFormat = "MMM d, yyyy", showTime = false }) {
	const { draft, setDraft } = useDateRangePicker();
	const [startDateStr, setStartDateStr] = React.useState(() => draft.from ? format(draft.from, dateDisplayFormat) : "");
	const [endDateStr, setEndDateStr] = React.useState(() => draft.to ?? draft.from ? format(draft.to ?? draft.from, dateDisplayFormat) : "");
	const startInputFocusedRef = React.useRef(false);
	const endInputFocusedRef = React.useRef(false);
	React.useEffect(() => {
		if (!startInputFocusedRef.current) setStartDateStr(draft.from ? format(draft.from, dateDisplayFormat) : "");
	}, [draft.from, dateDisplayFormat]);
	React.useEffect(() => {
		if (!endInputFocusedRef.current) setEndDateStr(draft.to ?? draft.from ? format(draft.to ?? draft.from, dateDisplayFormat) : "");
	}, [
		draft.from,
		draft.to,
		dateDisplayFormat
	]);
	const commitStartDateInput = () => {
		const ref = draft.from ?? /* @__PURE__ */ new Date();
		const parsed = tryParseDateString(startDateStr, dateDisplayFormat, ref);
		if (!parsed) {
			setStartDateStr(draft.from ? format(draft.from, dateDisplayFormat) : "");
			return;
		}
		setDraft((d) => {
			let from;
			if (showTime && d.from) {
				from = new Date(parsed);
				from.setHours(d.from.getHours(), d.from.getMinutes(), 0, 0);
			} else from = startOfDay(parsed);
			let to = d.to ?? (showTime ? from : endOfDay(from));
			if (from > to) to = showTime ? new Date(from) : endOfDay(from);
			return {
				from,
				to
			};
		});
	};
	const commitEndDateInput = () => {
		const ref = draft.to ?? draft.from ?? /* @__PURE__ */ new Date();
		const parsed = tryParseDateString(endDateStr, dateDisplayFormat, ref);
		if (!parsed) {
			setEndDateStr(draft.to ?? draft.from ? format(draft.to ?? draft.from, dateDisplayFormat) : "");
			return;
		}
		setDraft((d) => {
			const from = d.from ?? startOfDay(parsed);
			let to;
			if (showTime && (d.to ?? d.from)) {
				const base = d.to ?? d.from;
				to = new Date(parsed);
				to.setHours(base.getHours(), base.getMinutes(), 0, 0);
			} else to = endOfDay(parsed);
			let nextTo = to;
			if (nextTo < from) nextTo = showTime ? new Date(from) : endOfDay(from);
			return {
				from,
				to: nextTo
			};
		});
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-wrap gap-x-md gap-y-xs",
		children: [/* @__PURE__ */ jsxs("div", {
			className: "min-w-[200px] flex-1",
			children: [/* @__PURE__ */ jsx("p", {
				className: "m-0 text-body-xsmall leading-body-xsmall font-heading-medium text-text-secondary",
				children: "Start"
			}), /* @__PURE__ */ jsxs("div", {
				className: cn("mt-xxs flex items-end gap-xs", showTime ? "flex-wrap" : ""),
				children: [/* @__PURE__ */ jsx(TextInput, {
					className: "min-w-0 flex-1",
					placeholder: dateDisplayFormat,
					size: "base",
					value: startDateStr,
					onChange: (e) => setStartDateStr(e.target.value),
					onFocus: () => {
						startInputFocusedRef.current = true;
					},
					onBlur: () => {
						startInputFocusedRef.current = false;
						commitStartDateInput();
					},
					onKeyDown: (e) => {
						if (e.key === "Enter") e.target.blur();
					}
				}), showTime ? /* @__PURE__ */ jsx(TextInput, {
					className: "w-[120px] shrink-0",
					size: "base",
					type: "time",
					value: draft.from ? format(draft.from, "HH:mm") : "00:00",
					onChange: (e) => {
						const v = e.target.value;
						if (!draft.from) return;
						setDraft((d) => ({
							...d,
							from: applyTimeToDate(d.from, v)
						}));
					}
				}) : null]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			className: "min-w-[200px] flex-1",
			children: [/* @__PURE__ */ jsx("p", {
				className: "m-0 text-body-xsmall leading-body-xsmall font-heading-medium text-text-secondary",
				children: "End"
			}), /* @__PURE__ */ jsxs("div", {
				className: cn("mt-xxs flex items-end gap-xs", showTime ? "flex-wrap" : ""),
				children: [/* @__PURE__ */ jsx(TextInput, {
					className: "min-w-0 flex-1",
					placeholder: dateDisplayFormat,
					size: "base",
					value: endDateStr,
					onChange: (e) => setEndDateStr(e.target.value),
					onFocus: () => {
						endInputFocusedRef.current = true;
					},
					onBlur: () => {
						endInputFocusedRef.current = false;
						commitEndDateInput();
					},
					onKeyDown: (e) => {
						if (e.key === "Enter") e.target.blur();
					}
				}), showTime ? /* @__PURE__ */ jsx(TextInput, {
					className: "w-[120px] shrink-0",
					size: "base",
					type: "time",
					value: draft.to ?? draft.from ? format(draft.to ?? draft.from, "HH:mm") : "00:00",
					onChange: (e) => {
						const v = e.target.value;
						if (!(draft.to ?? draft.from)) return;
						setDraft((d) => ({
							...d,
							to: applyTimeToDate(d.to ?? d.from, v)
						}));
					}
				}) : null]
			})]
		})]
	});
}
function DateRangePickerCalendar({ numberOfMonths = 2 }) {
	const { calendarMountKey, draft, maxDate, setDraft } = useDateRangePicker();
	const calendarDisabled = React.useMemo(() => maxDate ? (date) => isAfter(startOfDay(date), startOfDay(maxDate)) : void 0, [maxDate]);
	const selectedRange = draft.from != null ? {
		from: draft.from,
		to: draft.to ?? draft.from
	} : void 0;
	return /* @__PURE__ */ jsx(Calendar, {
		className: "w-full min-w-0 !p-0",
		classNames: {
			root: "relative isolate w-full",
			month: "min-w-0 flex-1",
			month_grid: "mt-sm w-full border-collapse",
			months: "relative flex w-full min-w-0 flex-wrap gap-md md:flex-nowrap"
		},
		defaultMonth: draft.from ?? subMonths(/* @__PURE__ */ new Date(), 1),
		disabled: calendarDisabled,
		mode: "range",
		numberOfMonths,
		selected: selectedRange,
		variant: "flat",
		onSelect: (range) => {
			if (!range?.from) {
				setDraft({});
				return;
			}
			setDraft({
				from: range.from,
				to: range.to
			});
		}
	}, calendarMountKey);
}
function DateRangePickerFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex justify-end gap-xs", className),
		...props
	});
}
function DateRangePickerApply({ children = "Apply" }) {
	const { commit, draft } = useDateRangePicker();
	return /* @__PURE__ */ jsx(Button, {
		disabled: !draft.from,
		size: "small",
		type: "button",
		variant: "brand",
		onClick: () => commit(),
		children
	});
}
function DateRangePickerCancel({ children = "Cancel" }) {
	const { cancel } = useDateRangePicker();
	return /* @__PURE__ */ jsx(Button, {
		size: "small",
		type: "button",
		variant: "tertiary",
		onClick: cancel,
		children
	});
}
function DateRangePickerClear({ children = "Clear" }) {
	const { clear } = useDateRangePicker();
	return /* @__PURE__ */ jsx(Button, {
		size: "small",
		type: "button",
		variant: "tertiary",
		onClick: clear,
		children
	});
}
//#endregion
export { DateRangePicker, DateRangePickerApply, DateRangePickerCalendar, DateRangePickerCancel, DateRangePickerClear, DateRangePickerContent, DateRangePickerFields, DateRangePickerFooter, DateRangePickerPresets, DateRangePickerTrigger, useDateRangePicker };

//# sourceMappingURL=DateRangePicker.js.map